import {
  DataStateChangeEvent,
  PromptElementController,
  LinkBar,
  PromptOrientation,
} from '@sas-dvr/ltjs-bird';
import { EMPTY, Observable, of } from 'rxjs';
import {
  ignoreElements,
  map,
  tap,
  mergeMap,
  filter,
  distinctUntilChanged,
  skipWhile,
} from 'rxjs/operators';
import { ReportState } from '@sas-dvr/internal-va-react-core/store/rootReducer';
import { PromptElementImplInterface } from './PromptElementImplInterface';
import { getFormattedData, getRequired } from './utils';
import { PortableEvent } from '@sas-dvr/ltjs-commons';
import { ButtonBarState } from '@sas-dvr/internal-va-react-core/store/birdElement/buttonBarState';
import { StateObservable } from 'redux-observable';
import { AnyAction } from 'redux';

function rowIndicesFromIndex(index: number | null | undefined) {
  return index === null || index === undefined ? [] : [index];
}

export class ButtonBarImpl implements PromptElementImplInterface {
  private _controller: PromptElementController;
  private _id: string;
  private _ignoreValueChange: boolean = false;
  private _orientation: 'horizontal' | 'vertical' = 'horizontal';

  constructor(controller: PromptElementController, id: string, specialization: LinkBar) {
    this._controller = controller;
    this._id = id;
    this._orientation =
      specialization.getOrientation() === PromptOrientation.HORIZONTAL ? 'horizontal' : 'vertical';
  }

  createElement() {
    const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();

    const data = getFormattedData(this._controller);

    return ButtonBarState.actions.add(this._id, {
      label: data.label,
      name: data.name,
      valueColumnLabel: data.valueColumnLabel,
      measureColumnLabel: data.measureColumnLabel,
      required: getRequired(this._controller),
      selectedIndex: indices ? indices[0] : null,
      values: data.values,
      measureValues: data.measureValues,
      orientation: this._orientation,
    });
  }

  deleteElement() {
    return ButtonBarState.actions.delete(this._id);
  }

  handleDataChange(dataChange$: Observable<DataStateChangeEvent>) {
    return dataChange$.pipe(
      map(() => {
        const data = getFormattedData(this._controller);
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        return ButtonBarState.actions.update(this._id, {
          valueColumnLabel: data.valueColumnLabel,
          measureColumnLabel: data.measureColumnLabel,
          values: data.values,
          measureValues: data.measureValues,
          selectedIndex: indices ? indices[0] : null,
        });
      })
    );
  }

  handleValueChanged(valueChange$: Observable<PortableEvent>) {
    return valueChange$.pipe(
      mergeMap(() => {
        if (this._ignoreValueChange) {
          return EMPTY;
        }
        const indices = this._controller.getCurrentValuesInTermsOfOrderedUniqueRowIndices();
        return of(
          ButtonBarState.actions.update(this._id, {
            selectedIndex: indices ? indices[0] : null,
          })
        );
      })
    );
  }

  handleActions(_myActions$: Observable<AnyAction>, state$: StateObservable<ReportState>) {
    return state$.pipe(
      map((aState) => aState.birdElements.ButtonBar[this._id]),
      filter((myState): myState is Exclude<typeof myState, undefined> => myState !== undefined),
      map((myState) => myState.selectedIndex),
      distinctUntilChanged(),
      skipWhile((index) => index === null || index === undefined),
      tap((selectedIndex) => {
        this._ignoreValueChange = true;
        this._controller.setCurrentValuesInTermsOfOrderedUniqueRowIndices(
          rowIndicesFromIndex(selectedIndex)
        );
        this._ignoreValueChange = false;
      }),
      ignoreElements()
    );
  }
}
